import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Gift, Percent, Play, Calendar, CreditCard, Smartphone, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { redirectToRedsys } from "./Checkout";

const BENEFITS = [
  { icon: Gift, title: "Sorteos exclusivos", text: "Viajes internacionales, fines de semana premium y experiencias solo para socios." },
  { icon: Percent, title: "Ofertas directas", text: "Descuentos y prioridad en recechos, monterías y productos del club." },
  { icon: Play, title: "Video Club", text: "Documentales, crónicas y masterclasses de caza en exclusiva." },
  { icon: Calendar, title: "Prioridad de reserva", text: "Acceso anticipado a plazas limitadas en las mejores fincas." },
];

export default function HazteSocio() {
  const { user, refresh } = useAuth();
  const [method, setMethod] = useState("card");
  const [busy, setBusy] = useState(false);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/create", { kind: "membership", method });
      redirectToRedsys(data);
    } catch (e) {
      toast.error(formatError(e));
      setBusy(false);
    }
  };

  return (
    <div data-testid="hazte-socio-page">
      <section className="relative">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1606804235853-a2bdff23724b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxkZWVyJTIwZm9yZXN0fGVufDB8fHx8fDE3ODYwOTgwODh8MA&ixlib=rb-4.1.0&q=85" alt="Ciervo en el bosque" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/65" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-28 lg:py-36">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <p className="overline-gold mb-4 flex items-center gap-2"><Crown size={14} /> Membresía anual</p>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl text-white font-light tracking-tight hero-text-shadow">
              Hazte <span className="text-gold italic">Socio</span>
            </h1>
            <p className="mt-6 text-white/75 font-light hero-text-shadow max-w-xl">
              Una cuota. Todo el club. Sorteos exclusivos, ofertas, Video Club y la hermandad de Cinegética Ruta del Agua.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {BENEFITS.map((b, i) => (
              <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border-l border-gold/30 pl-5" data-testid={`benefit-${i}`}>
                <b.icon size={22} strokeWidth={1.25} className="text-gold mb-3" />
                <h3 className="text-lg text-white font-medium">{b.title}</h3>
                <p className="mt-2 text-white/55 text-sm font-light leading-relaxed">{b.text}</p>
              </motion.div>
            ))}
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="card-dark border-gold/25 p-8 lg:p-10" data-testid="membership-card">
            <p className="overline-gold">Cuota anual</p>
            <div className="mt-4 flex items-end gap-2">
              <span className="font-heading text-6xl text-white">100 €</span>
              <span className="text-white/40 text-sm mb-2">/ año</span>
            </div>
            <p className="mt-4 text-white/55 text-sm font-light">Sin permanencia oculta. Activa tu acceso al instante tras el pago.</p>

            {user && user.is_member ? (
              <div className="mt-8 border border-gold/40 bg-gold/5 p-5" data-testid="already-member">
                <p className="flex items-center gap-2 text-gold font-medium"><CheckCircle2 size={18} /> Ya eres socio activo</p>
                <p className="mt-2 text-white/60 text-sm">Membresía válida hasta: <b>{user.member_until?.slice(0, 10)}</b></p>
                <p className="mt-3 text-white/45 text-xs">Si renuevas ahora, se suman 365 días a tu fecha de vencimiento.</p>
              </div>
            ) : null}

            {user ? (
              <>
                <div className="mt-8 space-y-3">
                  <p className="text-xs uppercase tracking-[0.15em] text-white/40">Método de pago</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button onClick={() => setMethod("card")}
                      className={`border p-4 flex flex-col items-center gap-2 transition-colors duration-200 ${method === "card" ? "border-gold text-gold" : "border-white/15 text-white/50"}`}
                      data-testid="membership-method-card">
                      <CreditCard size={20} strokeWidth={1.5} />
                      <span className="text-[10px] uppercase tracking-[0.15em]">Tarjeta</span>
                    </button>
                    <button onClick={() => setMethod("bizum")}
                      className={`border p-4 flex flex-col items-center gap-2 transition-colors duration-200 ${method === "bizum" ? "border-gold text-gold" : "border-white/15 text-white/50"}`}
                      data-testid="membership-method-bizum">
                      <Smartphone size={20} strokeWidth={1.5} />
                      <span className="text-[10px] uppercase tracking-[0.15em]">Bizum</span>
                    </button>
                  </div>
                  {method === "bizum" && (
                    <p className="text-amber-400/80 text-xs" data-testid="membership-bizum-note">
                      Nota: en el entorno de pruebas, Bizum admite hasta 10 €. La cuota de 100 € debe pagarse con tarjeta en modo test.
                    </p>
                  )}
                </div>
                <button onClick={pay} disabled={busy} className="btn-gold-solid w-full mt-8 disabled:opacity-40" data-testid="membership-pay-button">
                  {busy ? "Conectando con el TPV..." : "Pagar 100 € y Activar Membresía"}
                </button>
                <p className="flex items-center justify-center gap-2 mt-4 text-[10px] text-white/35 uppercase tracking-[0.15em]">
                  <ShieldCheck size={13} /> Pago seguro vía Redsys (modo pruebas)
                </p>
              </>
            ) : (
              <div className="mt-8 space-y-4">
                <p className="text-white/60 text-sm font-light">Necesitas una cuenta para activar tu membresía.</p>
                <Link to="/login" state={{ from: "/hazte-socio" }} className="btn-gold-solid w-full" data-testid="membership-login-cta">
                  Crear Cuenta / Acceder
                </Link>
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
